<template>
    <v-container
            id="product"
            fluid
            tag="section"
    >

        <v-breadcrumbs :items="breadcrumb">
            <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                        :to="item.href"
                        :disabled="item.disabled"
                >
                    {{ item.text.toUpperCase() }}
                </v-breadcrumbs-item>
            </template>
        </v-breadcrumbs>

        <v-card :loading="loading ? 'white' : ''"
                v-if="!displaySuccess"
        >
            <v-overlay :value="loading" :absolute="true">
                <v-progress-circular
                    indeterminate
                    size="64"
                ></v-progress-circular>
            </v-overlay>
            <v-card-title class="black white--text">
                <span class="headline">{{product.title || $t('product.new_product')}}</span>
                <v-spacer></v-spacer>
            </v-card-title>

            <v-form
                    ref="form"
                    :disabled="!product.editable"
                    v-model="valid"
            >
                <v-container id="product-container">
                    <v-alert v-if="!product.fully_editable && product.editable"
                             color="primary"
                             outlined
                    >{{ $t('product.not_fully_editable') }}
                    </v-alert>

                    <template
                            v-if="product.woo_commerce_product_id"
                    >
                        <v-icon small>mdi-open-in-new</v-icon>&nbsp;
                        <a :href="'https://www.imparfaiteparis.com?post_type=product&p='+ product.woo_commerce_product_id"
                           target="_blank"
                        >{{$t('global.view_on_imparfaite')}}</a>
                    </template>

                    <v-row>
                        <v-col
                                cols="12"
                                md="auto"
                                v-for="(file, index) in product_images"
                                :key="index"
                        >
                            <div class="image-container">
                                <img :src="(file.name) ? getFileURL(file) : file"
                                     alt=""
                                     class="product-image"
                                     :class="{'front-image' : frontImageIndex === index}"
                                     @click="frontImageIndex = index"
                                />
                                <span v-if="frontImageIndex === index" class="front-image-label">Face</span>
                                <v-btn
                                        v-if="(!product.id || frontImageIndex !== index) && product.editable && product.fully_editable"
                                        color="error"
                                        depressed
                                        absolute
                                        top
                                        right
                                        fab
                                        small
                                        @click="deleteImage(file)"
                                >
                                    <v-icon dark>delete</v-icon>

                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col
                                v-if="product.editable && product.fully_editable"
                                cols="12"
                                md="12"
                        >
                            <v-file-input
                                    v-model="imagesInput"
                                    :label="$t('product.upload_images')"
                                    prepend-icon="mdi-camera"
                                    name="images"
                                    accept="image/*"
                                    multiple
                                    :placeholder="$t('product.images_information')"
                                    @change="onFileChange($event, 'images')"
                                    :loading="loadingImages"
                                    :rules="[rules.minFiles(3, 'images'), rules.maxFiles(6, 'images'), rules.sizeFile]"
                            ></v-file-input>
                        </v-col>

                        <v-col
                                cols="12"
                                md="12"
                        >
                            <v-textarea
                                    :label="$t('product.description')"
                                    :placeholder="$t('product.description_placeholder')"
                                    v-model="product.description"
                                    :rules="[rules.required]"
                                    rows="3"
                                    :disabled="!product.fully_editable"
                            />
                        </v-col>

                        <v-col
                                cols="12"
                                md="12"
                                v-if="product.fully_editable"
                        >
                            <v-select
                                    v-model="product.metadata.product_state"
                                    :label="$t('product.product_state')"
                                    :items="product_state"
                                    item-text="name"
                                    :rules="[rules.required]"
                                    return-object
                            >
                                <template slot="item" slot-scope="data">
                                    <div class="pa-4">
                                        {{ data.item.name }}<br/>
                                        <span class="caption text--secondary">{{ data.item.description }}</span> <br/>
                                        <span class="caption font-weight-light font-italic">{{ data.item.subdescription }}</span>
                                    </div>
                                </template>
                            </v-select>

                            <v-alert
                                    v-if="product.metadata.product_state && product.metadata.product_state.slug === 'mauvais-etat'"
                                    color="red"
                                    outlined
                            >
                                {{ $t('product.bad_condition_not_accepted') }}
                            </v-alert>
                        </v-col>

                        <v-col
                                cols="12"
                                md="12"
                                v-if="(product.editable && product.fully_editable) || product.defect_images.length || product.defects"
                        >
                            <v-switch
                                    v-if="!hasDefects"
                                    :disabled="!product.editable || !product.fully_editable"
                                    v-model="showDefect"
                                    inset
                                    :label="$t('product.add_defect')"
                                    color="secondary">
                            </v-switch>

                            <v-textarea
                                    :label="$t('product.defect')"
                                    v-model="product.defects"
                                    v-if="showDefect || product.defects"
                            />

                            <div
                                    v-for="(file, index) in product_defect_images"
                                    :key="index"
                                    class="image-container">
                                <img :src="(file.name) ? getFileURL(file) : file" alt="" class="product-image"/>
                                <v-btn
                                        v-if="product.editable && product.fully_editable"
                                        color="error"
                                        depressed
                                        absolute
                                        top
                                        right
                                        fab
                                        small
                                        @click="deleteImage(file, 'defect_images')"
                                >
                                    <v-icon dark>delete</v-icon>

                                </v-btn>
                            </div>

                            <v-file-input
                                    v-if="showDefect && product.editable && product.fully_editable"
                                    v-model="imagesDefectInput"
                                    :label="$t('product.upload_defect_images')"
                                    prepend-icon="mdi-camera"
                                    name="defect_images"
                                    accept="image/*"
                                    multiple
                                    :placeholder="$t('product.images_defect_information')"
                                    :rules="[rules.minFiles(1, 'defect_images'), rules.maxFiles(3, 'defect_images'), rules.sizeFile]"
                                    @change="onFileChange($event, 'defect_images')"
                                    :loading="loadingImagesDefect"
                            ></v-file-input>

                        </v-col>
                    </v-row>

                    <v-row
                            align="center"
                    >
                        <v-col
                                cols="12"
                                md="3"
                        >
                            <v-text-field
                                    :label="$t('product.price')"
                                    type="number"
                                    v-model="product.price"
                                    :rules="[rules.required]"
                            />
                        </v-col>
                            <v-col v-if="product.discount_rate_proposal"
                                cols="12"
                                md="6"
                            ><v-container fluid>
                                <v-card color="#f9dbc2">
                                    <v-card-title class="text-center">Suggestion de soldes
                                        <v-spacer></v-spacer>
                                        <v-tooltip left top>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs"
                                                        v-on="on">mdi-information</v-icon>
                                            </template>
                                            <span>Opération Archives du {{ product.discount_start_datetime | momentDateSimple }} au {{ product.discount_end_datetime | momentDateSimple }}</span>
                                        </v-tooltip>

                                    </v-card-title>
                                    <v-card-text class="text-center font-weight-medium">
                                        <v-row>
                                            <v-col cols="6" md="6">
                                                <v-select
                                                    v-model="product.discount_rate_proposal"
                                                    :items="discountRatesProposalList"
                                                    item-text="label"
                                                    item-value="value"
                                                    label="Réduction"
                                                    hide-details
                                                    prepend-icon="sale"
                                                    single-line
                                                    @change="calculateFDiscountPriceFromDiscountRate(product.discount_rate_proposal, product)"
                                                ></v-select>
                                            </v-col>
                                            <v-col cols="6" md="6" class="pink--text text--darken-2">
                                                soit {{ calculateFDiscountPriceFromDiscountRate(product.discount_rate_proposal, product.price) | toCurrency }}
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                    <v-card-actions>
                                        <v-spacer></v-spacer>
                                        <v-btn icon
                                               @click="dismissDiscountOnProduct(product)"
                                        >
                                            <v-icon color="red darken-2" x-large>mdi-close-circle</v-icon>
                                        </v-btn>
                                        <v-spacer></v-spacer>
                                        <v-btn icon
                                               @click="applyDiscountOnProduct(product)"
                                        >
                                            <v-icon color="green darken-2" x-large>mdi-check-circle</v-icon>
                                        </v-btn>
                                        <v-spacer></v-spacer>
                                    </v-card-actions>
                                </v-card>
                            </v-container>
                            </v-col>
                            <v-col v-else-if="product.discount_start_datetime"
                                cols="12"
                                md="6"
                            >
                                <v-container fluid>
                                    <v-card color="blue-grey lighten-5">
                                        <v-card-title class="text-center">Soldes
                                            <v-spacer></v-spacer>
                                            <v-tooltip left top>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon v-bind="attrs"
                                                            v-on="on">mdi-information</v-icon>
                                                </template>
                                                <span>Opération Archives du {{ product.discount_start_datetime | momentDateSimple }} au {{ product.discount_end_datetime | momentDateSimple }}</span>
                                            </v-tooltip>

                                        </v-card-title>
                                        <v-card-text class="text-center font-weight-medium">
                                            <v-row>
                                                <v-col cols="6" md="6">
                                                    <v-select
                                                        v-model="product.discount_rate"
                                                        :items="discountRatesList"
                                                        item-text="label"
                                                        item-value="value"
                                                        label="Réduction"
                                                        hide-details
                                                        prepend-icon="sale"
                                                        single-line
                                                        @change="calculateFDiscountPriceFromDiscountRate(product.discount_rate, product)"
                                                    ></v-select>
                                                </v-col>
                                                <v-col cols="6" md="6" class="pink--text text--darken-2">
                                                    soit {{ calculateFDiscountPriceFromDiscountRate(product.discount_rate, product.price) | toCurrency }}
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                    </v-card>
                                </v-container>
                            </v-col>
                    </v-row>

                    <v-row v-if="product.category_id && product.fully_editable">
                        <v-col cols>
                            <strong>{{ $t('product.category_dots') }}</strong> {{currentCategory().name}}
                            <v-btn
                                    @click="product.category_id = '';selectedCategories=[];"
                                    color="secondary"
                                    tile
                                    depressed
                                    small
                            >
                                {{ $t('product.edit') }}
                            </v-btn>
                        </v-col>
                    </v-row>

                    <v-row v-if="!product.category_id && product.fully_editable">
                        <v-col
                                cols="12"
                                md="3"
                                v-for="index in countCategoriesLevels"
                                :key="index"
                        >
                            <v-select
                                    v-model="selectedCategories[index-1]"
                                    :label="(index-1) ? $t('product.subcategory') : $t('product.category')"
                                    :items="(index === 1) ? formattedCategories : ((selectedCategories[index-2]) ? selectedCategories[index-2].children : [])"
                                    :loading="loadingCategories"
                                    item-text="name"
                                    :rules="[rules.required]"
                                    return-object
                            >
                            </v-select>
                        </v-col>
                    </v-row>

                    <v-row>
                        <template
                                v-for="category in availableTaxonomies"
                        >
                            <v-col
                                    v-if="category.id !== 'product_state' && category.id !== 'product_measures' && category.id !== 'product_vendor' && formConfig[category.id] && category.values.length"
                                    cols="12"
                                    md="4"
                                    :key="category.id"
                            >
                                <v-select
                                        :key="category.id + '_2'"
                                        :items="category.values"
                                        item-text="name"
                                        v-model="product.metadata[category.id]"
                                        :chips="formConfig[category.id].multiple"
                                        :small-chips="formConfig[category.id].multiple"
                                        :no-data-text="$t('global.no_data')"
                                        :label="category.name"
                                        :multiple="formConfig[category.id].multiple"
                                        :rules="formConfig[category.id].required ? [rules.required] : []"
                                        deletable-chips
                                        return-object
                                >
                                </v-select>
                            </v-col>
                        </template>
                    </v-row>

                    <v-row>
                        <v-col cols="12"
                               class="pb-0"
                               v-if="measures.length">
                            <h3>{{$t('product.measures')}}</h3>
                        </v-col>
                        <v-col
                                cols="12"
                                md="4"
                                v-for="measure in measures"
                                :key="measure.id">
                            <v-text-field
                                    :value="product.metadata[measure.slug] ? product.metadata[measure.slug].name: ''"
                                    @change="onMeasureChange(measure, $event)"
                                    :label="measure.name"
                                    :rules="[rules.required]"
                                    :disabled="!product.fully_editable"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col
                                cols="12"
                        >
                            <v-btn
                                    @click="save"
                                    color="primary"
                                    tile
                                    depressed
                                    :disabled="!product.editable"
                            >
                                <template v-if="!product.id">{{$t('product.save_product')}}</template>
                                <template v-if="product.id">{{$t('product.update_product')}}</template>
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-form>
        </v-card>

        <v-card v-if="displaySuccess"
                class="pa-6 text-center">

            <div class="text-h1">👀</div>
            <h1>{{$t('product.success_title')}}</h1>
            <p>{{$t('product.sucesss_text')}}</p>
            <v-btn color="primary"
                   depressed
                   tile
                   to="/products/new">
                {{$t('product.add_piece')}}
            </v-btn>
            <v-btn color="secondary"
                   depressed
                   tile
                   to="/products">
                {{$t('product.view_my_pieces')}}
            </v-btn>
        </v-card>

        <v-snackbar
                v-model="snackbar"
                :color="color"
                timeout="-1"
        >
            {{ message }}
            <v-btn
                    dark
                    text
                    @click="snackbar = false"
            >
                {{$t('close')}}
            </v-btn>
        </v-snackbar>
    </v-container>
</template>

<script>
    import formConfig from '../../../config/new-product-form'
    import {mapState} from "vuex";

    export default {
        name: 'Product',

        async beforeMount() {
            await this.loadCategories();
            await this.load();
        },
        mounted() {
            // Temp: fix for v-file-input not triggering properly
            // https://github.com/vuetifyjs/vuetify/issues/10832
            let inputs = document.querySelectorAll('.v-file-input input');
            [...inputs].forEach((input) => {
                input.remove();
            })
        },
        data: function () {
            return {
                discountRatesProposalList: [
                    {'label': '-10%', 'value': 10},
                    {'label': '-20%', 'value': 20},
                    {'label': '-30%', 'value': 30},
                    {'label': '-40%', 'value': 40},
                    {'label': '-50%', 'value': 50},
                    {'label': '-60%', 'value': 60},
                    {'label': '-70%', 'value': 70},
                ],
                discountRatesList: [
                    {'label': 'Pas de réduction', 'value': null},
                    {'label': '-10%', 'value': 10},
                    {'label': '-20%', 'value': 20},
                    {'label': '-30%', 'value': 30},
                    {'label': '-40%', 'value': 40},
                    {'label': '-50%', 'value': 50},
                    {'label': '-60%', 'value': 60},
                    {'label': '-70%', 'value': 70},
                ],
                displaySuccess: false,
                formConfig,
                imagesInput: [],
                imagesDefectInput: [],
                showDefect: false,
                snackbar: false,
                color: 'info',
                message: '',
                filesToUpload: [],
                frontImageIndex: 0,
                product: {
                    editable: true,
                    fully_editable: true,
                    metadata: {},
                    images: [],
                    defect_images: []
                },
                valid: false,
                loading: false,
                loadingImages: false,
                loadingImagesDefect: false,
                availableTaxonomies: [],
                selectedCategories: [],
                loadingCategories: false,
                rules: {
                    required: value => {
                        if (!value || (Array.isArray(value) && !value.length)) {
                            return this.$i18n.t('required');
                        }
                        return true;
                    },
                    maxFiles: (n, type) => {
                        return v => ((v || '').length + this['product_' + type].length <= n) || this.$i18n.t('global.max_exceeded');
                    },
                    minFiles: (n, type) => {
                        return v => ((v || '').length + this['product_' + type].length >= n) || this.$i18n.t('global.min_files');
                    },
                    sizeFile: files => !files || !files.some(file => file.size > 10097152) || 'Vous ne pouvez pas envoyer de photos de plus de 10Mo'
                },
                categories: [],
                breadcrumb: [
                    {
                        text: this.$i18n.t('global.products'),
                        disabled: false,
                        href: '/products',
                    }
                ],
            };
        },

        methods: {
            load() {
                if (this.$route.params.id) {
                    this.loading = true;
                    this.$axios.get('products/' + this.$route.params.id)
                        .then(response => {
                            this.loading = false;
                            this.product = response.data;
                            this._formatProductMetadataFromAPI();
                            this.showDefect = this.hasDefects;
                            this.breadcrumb.push({
                                text: this.product.title,
                                disabled: false,
                            });
                        });
                } else {
                    this.breadcrumb.push({
                        text: this.$i18n.t('product.new_product'),
                        disabled: true,
                    });
                }
            },
            async loadCategories() {
                this.loadingCategories = true;
                await this.$axios.get('categories')
                    .then(response => {
                        this.loadingCategories = false;
                        this.categories = response.data
                            .map(category => {
                                category.metadata = category.metadata.map(taxonomy => {
                                    taxonomy.id = taxonomy.id.toString();
                                    taxonomy.values = Object.values(taxonomy.values);
                                    return taxonomy;
                                });
                                return category;
                            });
                    });
            },
            calculateFDiscountPriceFromDiscountRate(discountRate, price) {
                if (discountRate === null) {
                    return price;
                }

                return price - (price * discountRate / 100);
            },
            applyDiscountOnProduct(product) {
                this.loading = true;
                this.$axios.post('products/' + product.id + '/discounts/applies', {
                    'discount_rate' : null,
                    'discount_rate_proposal' : product.discount_rate_proposal
                })
                    .then(() => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('product.product_saved');
                        this.color = 'success';
                        this.loading = false;
                        this.load();
                    })
                    .catch((error) => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('product.update_failed') + ' ' + error.response.data['hydra:description'];
                        this.color = 'error';
                        this.loading = false;
                        this.load();
                    });
            },
            dismissDiscountOnProduct(product) {
                this.loading = true;
                this.$axios.post('products/' + product.id + '/discounts/dismiss', {})
                    .then(() => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('product.product_saved');
                        this.color = 'success';
                        this.loading = false;
                        this.load();
                    })
                    .catch((error) => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('product.update_failed') + ' ' + error.response.data['hydra:description'];
                        this.color = 'error';
                        this.loading = false;
                        this.load();
                    });
            },
            save() {
                this.$refs.form.validate();
                // Check if the form is valid and the images count are corrects.
                if (!this.valid || this.product_images.length < 3 || this.product_images.length > 6
                    || (this.product.defects && (this.product_defect_images.length < 1 || this.product_defect_images.length > 3))) {
                    return false;
                }

                if (this.loadingImages || this.loadingImagesDefect) {
                    return false;
                }

                const product = {
                    ...this.product,
                    photos: null,
                    taxonomies: null,
                    category: null,
                    price: parseFloat(this.product.price),
                    store_id: this.userInformation.store_id,
                    metadata: this._formatMetadataToAPI()
                };

                // Put the front image in first position.
                if (this.frontImageIndex > 0) {
                    const frontImage = this.product.images[this.frontImageIndex];
                    this.product.images.splice(this.frontImageIndex, 1);
                    this.product.images.unshift(frontImage);
                    this.frontImageIndex = 0;
                }

                this.loading = true;
                let promise;
                if (this.$route.params.id) {
                    if (product.status === 'REJECTED') {
                        promise = this.$axios.put('products/' + this.$route.params.id, product)
                            .then((response) => {
                                return this.$axios.post('products/' + response.data.id + '/reviews', {}).then(() => {
                                    this.loading = false;
                                    this.snackbar = true;
                                    this.message = this.$i18n.t('product.product_saved');
                                    this.color = 'info';
                                    this.$router.push({path: `/products/${response.data.id}`});
                                });
                            });
                    } else {
                        promise = this.$axios.put('products/' + this.$route.params.id, product)
                            .then(() => {
                                this.loading = false;
                                this.snackbar = true;
                                this.message = this.$i18n.t('product.product_saved');
                                this.color = 'info';
                            });
                    }

                } else {
                    promise = this.$axios.post('products', product)
                        .then((response) => {
                            return this.$axios.post('products/' + response.data.id + '/reviews', {}).then(() => {
                                this.loading = false;
                                this.displaySuccess = true;
                                this.$router.push({path: `/products/${response.data.id}`});
                            });
                        });
                }

                return promise.catch((error) => {
                    this.snackbar = true;
                    this.loading = false;
                    this.message = this.$i18n.t('product.update_failed');
                    this.color = 'error';
                    console.error(error);
                });

            },
            deleteImage(img, type = 'images') {
                const index = this.product[type].indexOf(img);
                if (index !== -1) {
                    this.product[type].splice(index, 1);
                } else {
                    const index2 = this.filesToUpload.indexOf(img);
                    this.filesToUpload.splice(index2, 1);
                }
            },
            getFileURL(file) {
                return URL.createObjectURL(file)
            },
            onFileChange(files, type) {
                if (!files || !files.length)
                    return;

                if ((type === 'images' && this.product.images.length + files.length > 6) ||
                    (type === 'defect_images' && this.product.defect_images.length + files.length > 3)) {
                    return;
                }

                if (type === 'images') {
                    this.loadingImages = true;
                    this.imagesInput = [];
                } else {
                    this.loadingImagesDefect = true;
                    this.imagesDefectInput = [];
                }

                files = files.map(file => {
                    file.imageType = type;
                    return file;
                });

                this.filesToUpload = this.filesToUpload.concat(files);

                this.uploadImages().then(() => {
                    this.loadingImages = false;
                    this.loadingImagesDefect = false;
                });
            },
            uploadImages() {
                const promises = [];
                this.filesToUpload.forEach((file) => {
                    let formData = new FormData();
                    formData.append('image', file);
                    promises.push(this.$axios.post('images', formData, {headers: {'Content-Type': 'multipart/form-data'}}));
                });

                return Promise
                    .allSettled(promises)
                    .then((responses) => {
                        responses.forEach((response, index) => {
                            if (response.status === 'fulfilled') {
                                const type = this.filesToUpload[index].imageType;
                                this.product[type].push(response.value.data.url);
                            } else {
                                this.snackbar = true;
                                this.message = this.$i18n.t('orders.transfer_failed');
                                this.color = 'error';
                            }
                        });
                        this.filesToUpload = [];
                    })
                    .catch(() => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('orders.transfer_failed');
                        this.color = 'error';
                    });
            },
            _getChildrenCategories(category) {
                return this.categories
                    .filter(child_category => child_category.parent === category.id)
                    .map(category => {
                        category.children = this._getChildrenCategories(category);
                        return category;
                    });
            },
            _formatProductMetadataFromAPI() {
                const metadata = {};
                this.product.metadata.forEach(meta => {
                    meta.name = meta.value;
                    delete meta.value;
                    let kind = meta.kind;
                    if(meta.kind === 'product_measures'){
                        kind = meta.slug;
                        meta.isMeasure = true;
                    }
                    if (metadata[kind] === undefined) {
                        metadata[kind] = meta;
                    } else {
                        if (!Array.isArray(metadata[kind])) {
                            metadata[kind] = [metadata[kind]];
                        }
                        metadata[kind].push(meta);
                    }
                });
                this.product.metadata = metadata;
                const categories = this.categories.find(cat => cat.id === this.product.category_id);
                this.availableTaxonomies = categories ? categories.metadata : [];
            },
            _formatMetadataToAPI() {
                const oldValue = {...this.product.metadata};
                let newValue = [];
                for (let taxonomy in oldValue) {
                    let metas = oldValue[taxonomy];
                    if (!Array.isArray(metas)) {
                        metas = [metas];
                    }
                    metas = metas
                        .map(meta => {
                            meta.value = meta.name;
                            meta.kind = meta.isMeasure ? 'product_measures' : taxonomy;
                            return meta;
                        });
                    newValue = newValue.concat(metas);
                }
                return newValue;
            },
            onMeasureChange(measure, event) {
                this.product.metadata[measure.slug] = {
                    isMeasure: true,
                    id: measure.id,
                    slug: measure.slug,
                    name: event
                };
            },
            currentCategory() {
                return this.categories.find(category => category.id === this.product.category_id);
            }
        },

        computed: {
            ...mapState(['userInformation']),
            product_images() {
                return this.product.images.concat(this.filesToUpload.filter(file => file.imageType === 'images'));
            },
            product_defect_images() {
                return this.product.defect_images.concat(this.filesToUpload.filter(file => file.imageType === 'defect_images'));
            },
            formattedCategories() {
                let categories = this.categories
                    .filter(category => category.parent === 0)
                    .map(category => {
                        category.children = this._getChildrenCategories(category);
                        return category;
                    })
                    .filter(category => category.children.length);

                // Remove top level categories
                let formatted = [];
                categories.forEach(cat => {
                    formatted = formatted.concat(cat.children);
                });

                return formatted;
            },
            countCategoriesLevels() {
                if (this.selectedCategories.length === 0) return 1;
                const lastCategory = this.selectedCategories[this.selectedCategories.length - 1];
                return this.selectedCategories.length + (lastCategory.children && lastCategory.children.length ? 1 : 0)
            },
            measures() {
                const measures = this.availableTaxonomies.find(taxo => taxo.id === 'product_measures');
                if (measures) return measures.values;
                return [];
            },
            hasDefects() {
                return this.product_defect_images.length || this.product.defects;
            },
            product_state() {
                if (!this.categories.length) return;
                return [
                    {
                        id: 16,
                        slug: 'tres-bon-etat',
                        name: this.$i18n.t('product.state.very_good_condition'),
                        description: this.$i18n.t('product.state.very_good_condition_description'),
                        subdescription: this.$i18n.t('product.state.very_good_condition_sub_description'),
                    },
                    {
                        id: 136,
                        slug: 'bon-etat',
                        name: this.$i18n.t('product.state.good_condition'),
                        description: this.$i18n.t('product.state.good_condition_description'),
                        subdescription: this.$i18n.t('product.state.good_condition_sub_description'),
                    },
                    {
                        id: 882,
                        slug: 'etat-correct',
                        name: this.$i18n.t('product.state.correct_condition'),
                        description: this.$i18n.t('product.state.correct_condition_description'),
                        subdescription: this.$i18n.t('product.state.correct_condition_sub_description'),
                    },
                    {
                        id: 0,
                        slug: 'mauvais-etat',
                        name: this.$i18n.t('product.state.bad_condition'),
                        description: this.$i18n.t('product.state.bad_condition_description'),
                        subdescription: this.$i18n.t('product.state.bad_condition_sub_description'),
                    }
                ];
            }
        },
        watch: {
            selectedCategories: {
                handler() {
                    this.product.category_id = '';
                    const product_state = this.product.metadata.product_state;
                    this.product.metadata = {product_state};
                    this.availableTaxonomies = [];
                    if (this.countCategoriesLevels === this.selectedCategories.length) {

                        const lastCategory = this.selectedCategories[this.selectedCategories.length - 1];

                        this.product.category_id = lastCategory.id;
                        this.availableTaxonomies = lastCategory.metadata.filter(item => Object.keys(item.values).length > 0);

                    }
                }
            },
        }
    }
</script>

<style lang="scss" scoped>

    .image-container {
        height: 270px;
        position: relative;
        display: inline-block;
        margin-right: 10px;
        margin-bottom: 10px;
        img {
            border: 5px solid transparent;
            cursor: pointer;
        }
        .v-btn {
            margin: 0 !important;
            right: -20px !important;
        }
    }

    .product-image {
        height: 250px;
    }

    #product-container {
        padding: 30px;
    }

    .col- * {
        padding: 0 12px;
    }

    .front-image {
        border: 5px solid #e9c09f !important;
        position: relative;
    }

    .front-image-label {
        display: block;
        width: 25%;
        margin: auto;
        position: relative;
        top: -1.4rem;
        background: #e9c09f;
        text-align: center;
        color: white;
        font-weight: 500;
    }

</style>
